import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  getPendingRegistration,
  registerAccount,
  logoutSession,
  fetchCurrentUser,
} from '../api/endpointsBackend';
import { refreshAccessToken, clearToken, setToken } from '../auth/tokenService';
import {
  CLEANROOM_AUTH_ENABLED,
  clearSession as clearCleanroomSession,
  getStoredToken as getCleanroomToken,
} from '../auth/cleanroomAuth';
import { safeReturnTo } from '../utils/returnTo';

const AuthContext = createContext(null);

const API = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsRegistration, setNeedsRegistration] = useState(false);
  const [pendingIdentity, setPendingIdentity] = useState(null);

  /**
   * Establish who the caller is.
   *
   * Order matters: the refresh cookie is the only durable evidence of a
   * session, so it is redeemed first. A 401 there is the normal signed-out
   * state, not an error — but it may still mean a *pending* registration, so
   * that is checked before giving up.
   */
  const bootstrap = useCallback(async () => {
    setLoading(true);
    try {
      if (CLEANROOM_AUTH_ENABLED) {
        const cleanroomToken = getCleanroomToken();
        if (!cleanroomToken) { setUser(null); return; }
        // The cleanroom token is minted by /system/token and parked in
        // localStorage; the axios client reads from tokenService. Without this
        // hand-off every request goes out unsigned and 401s into /login.
        setToken(cleanroomToken);
        setUser(await fetchCurrentUser());
        return;
      }

      const token = await refreshAccessToken();
      if (token) {
        setUser(await fetchCurrentUser());
        setNeedsRegistration(false);
        return;
      }

      setUser(null);
      try {
        setPendingIdentity(await getPendingRegistration());
        setNeedsRegistration(true);
      } catch {
        setNeedsRegistration(false);
        setPendingIdentity(null);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  const login = (returnTo) => {
    if (CLEANROOM_AUTH_ENABLED) {
      window.location.assign('/login_cleanroom');
      return;
    }
    // A full-page navigation, not fetch: the backend answers with a 302 to
    // Google's consent screen, which the browser must follow itself.
    const target = returnTo || window.location.pathname + window.location.search;
    // A same-site path that is not an auth page, or the start page. SignupPage
    // applies the same check when it sends a new account back to this target.
    const safe = safeReturnTo(target) || '/';
    window.location.assign(
      `${API}/api/v1/auth/google/login?return_to=${encodeURIComponent(safe)}`
    );
  };

  // `redirectTo` is where the signed-out page lands, /login by default. The
  // invitation page passes its own URL when the wrong account opened it, so
  // the person is back on the invitation, one click from choosing another.
  // Anything `safeReturnTo` refuses lands on /login instead: an off-site URL,
  // or the click event when `logout` is wired straight to an onClick.
  const logout = async (redirectTo) => {
    const destination = safeReturnTo(redirectTo) || '/login';
    setUser(null);
    setNeedsRegistration(false);
    clearToken();

    if (CLEANROOM_AUTH_ENABLED) {
      clearCleanroomSession();
      window.location.assign('/login_cleanroom');
      return;
    }

    // Revoke server-side before dropping the cookie, so the session dies even
    // if the browser keeps a copy. Failure here must not trap the user on a
    // page they think they have left.
    try { await logoutSession(); } catch { /* best effort */ }

    // Deliberately keep `mikapital_active_workspace_id`. Removing it
    // re-introduces the race where early API calls fire before WorkspaceProvider
    // registers its getter and fail with a 422 missing-header. WorkspaceContext
    // re-validates the persisted id against the new user's memberships anyway.
    window.location.assign(destination);
  };

  // Called from SignupPage on arrival — Google's consent screen already asked.
  // Note what this does NOT do on failure: `needsRegistration` and
  // `pendingIdentity` are cleared only after the account exists, so a failed
  // attempt leaves the signup screen standing with the identity still on it,
  // rather than bouncing the person back to /login with nothing explained.
  const register = async () => {
    setLoading(true);
    try {
      const created = await registerAccount();
      await refreshAccessToken(); // the register response set the session cookie
      setUser(created);
      setNeedsRegistration(false);
      setPendingIdentity(null);
      return created;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    needsRegistration,
    pendingIdentity,
    login,
    logout,
    register,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
